import { Suspense, lazy, use, useEffect } from "react";
import { AuthContext } from "react-oauth2-code-pkce";
import { type IAuthContext } from "react-oauth2-code-pkce/dist/types";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./Pages/Login/Login.tsx";
import Logout from "./Pages/Logout/Logout.tsx";
import Unauthorized from "./Pages/Unauthorized/Unauthorized.tsx";
import SessionExpired from "./Pages/Unauthorized/SessionExpired.tsx";

import { useUserAuth } from "./utils/useUserAuth";
import {
  LOGOUT,
  UNAUTHORIZED,
  LANDING,
  MDM,
  MDM_VIEW_SITES,
  PARTNER,
  PARTNER_VIEW_CUSTOMERS,
  PRICING,
  PRICING_FUEL_PRICES,
  REPORTS,
  ADMIN,
  CONTRACT_PARAMETERS,
  CONTRACT_PARAMETERS_PARTNER,
  GSAP_VOLUMES,
  GSAP_VOLUMES_VOLUMES,
  PRODUCT_DATA,
  REGION_DATA,
  SITE_MISCELLANEOUS,
  SITE_MISCELLANEOUS_VAT_RATES,
  SITE_MISCELLANEOUS_PLANT_DETAILS,
  SITE_MISCELLANEOUS_AUXILIARY_SITE_INFO,
  VAT_RATES,
  PLANT_DETAILS,
  AUXILIARY_SITE_INFO,
  FUEL_REBATES,
  LOYALTY_REBATES,
  REPORTS_ACCESS_DECLARATION,
  PLUS_CARD,
  PLUS_CARD_CARDS_DOWNLOADS,
} from "./routes/paths.ts";
import ProtectedRoute from "./ProtectedRoute.tsx";
import Layout from "./components/Layout/Layout.tsx";

import { InactivityTracker } from "./components/InactivityTracker";
import Loader from "./components/Loader/Loader.tsx";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Reusable redirect component for unauthorized access
const RedirectToLanding = () => <Navigate to={LANDING} replace />;

const Landing = lazy(() => import("./Pages/Landing/Landing.tsx"));
const Clusters = lazy(() => import("./Pages/Clusters/ClustersWrapper.tsx"));
const Pricing = lazy(() => import("./Pages/Pricing/Pricing.tsx"));
const ReportGeneration = lazy(
  () => import("./Pages/Reports/ReportGeneration.tsx"),
);
const Reports = lazy(() => import("./Pages/Reports/Reports.tsx"));
const Admin = lazy(() => import("./Pages/Admin/Admin.tsx"));
const Partners = lazy(() => import("./Pages/Partners/PartnersWrapper.tsx"));
const GSAPVolumes = lazy(() => import("./Pages/GSAPVolumes/GSAPVolumes.tsx"));
const VatRates = lazy(() => import("./Pages/VatRates/VatRates.tsx"));
const PlantDetails = lazy(
  () => import("./Pages/PlantDetails/PlantDetails.tsx"),
);
const AuxiliarySiteInfo = lazy(
  () => import("./Pages/AuxiliarySiteInfo/AuxiliarySiteInfo.tsx"),
);
const SiteMiscellaneous = lazy(
  () => import("./Pages/SiteMiscellaneous/SiteMiscellaneous.tsx"),
);
const Volumes = lazy(() => import("./Pages/GSAPVolumes/Volumes.tsx"));
const Rebates = lazy(() => import("./Pages/GSAPVolumes/Rebates.tsx"));
const Correction = lazy(() => import("./Pages/GSAPVolumes/Correction.tsx"));
const GSAPImport = lazy(() => import("./Pages/GSAPVolumes/GSAPImport.tsx"));
const PartnersTable = lazy(() => import("./Pages/Partners/PartnersTable.tsx"));
const SitesTable = lazy(() => import("./Pages/Clusters/SitesTable.tsx"));
const ImportSites = lazy(() => import("./Pages/Clusters/ImportSites.tsx"));
const ImportSiteLogs = lazy(
  () => import("./Pages/Clusters/ImportSiteLogs.tsx"),
);
const CompiledSiteDataTable = lazy(
  () => import("./Pages/Clusters/CompiledSiteDataTable.tsx"),
);
const ImportCustomers = lazy(
  () => import("./Pages/Partners/ImportCustomers.tsx"),
);
const ImportLogs = lazy(() => import("./Pages/Partners/ImportLogs.tsx"));
const PartnerLevelContract = lazy(
  () => import("./Pages/ContractParameters/PartnerLevelContract.tsx"),
);
const SiteLevelContract = lazy(
  () => import("./Pages/ContractParameters/SiteLevelContract.tsx"),
);
const ProductLevelContract = lazy(
  () => import("./Pages/ContractParameters/ProductLevelContract.tsx"),
);
const ContractParameterImport = lazy(
  () => import("./Pages/ContractParameters/ContractParameterImport.tsx"),
);
const ContractParameterImportLogs = lazy(
  () => import("./Pages/ContractParameters/ImportLogs.tsx"),
);
const OMRFuelPriceView = lazy(
  () => import("./Pages/Pricing/OMRFuelPriceView.tsx"),
);
const OMRImportPrices = lazy(
  () => import("./Pages/Pricing/OMRImportPrices.tsx"),
);
const ImportOMRPriceLogs = lazy(
  () => import("./Pages/Pricing/ImportOMRPriceLogs.tsx"),
);
const AdBlueLPGWrapper = lazy(
  () => import("./Pages/Pricing/AdBlueLPGWrapper.tsx"),
);
const DeclarationReport = lazy(
  () => import("./Pages/Reports/DeclarationReport.tsx"),
);
const DealerReport = lazy(() => import("./Pages/Reports/DealerReport.tsx"));
const SiteOverviewReport = lazy(
  () => import("./Pages/Reports/SiteOverviewReport.tsx"),
);
const DetailedReport = lazy(() => import("./Pages/Reports/DetailedReport.tsx"));
const RebatesReport = lazy(() => import("./Pages/Reports/RebatesReport.tsx"));
const NextGenReport = lazy(
  () => import("./Pages/Reports/NextGenReport/index.tsx"),
);
const NotFound = lazy(() => import("./Pages/NotFound/NotFound.tsx"));
const ContractParameters = lazy(
  () => import("./Pages/ContractParameters/ContractParameters.tsx"),
);
const ProductData = lazy(() => import("./Pages/ProductData/ProductData.tsx"));
const RegionData = lazy(() => import("./Pages/RegionData/RegionData.tsx"));
const FuelRebates = lazy(() => import("./Pages/FuelRebates/FuelRebates.tsx"));
const LoyaltyRebates = lazy(
  () => import("./Pages/FuelRebates/LoyaltyRebates.tsx"),
);
const GSAPImportLogs = lazy(() => import("./Pages/GSAPVolumes/GSAPLogs.tsx"));
const ManualCorrections = lazy(
  () => import("./Pages/GSAPVolumes/ManualCorrections.tsx"),
);
const CardList = lazy(() => import("./Pages/PlusCard/CardList.tsx"));
const ImportCards = lazy(() => import("./Pages/PlusCard/ImportCards.tsx"));
const Transactions = lazy(() => import("./Pages/PlusCard/Transactions.tsx"));
const Downloads = lazy(() => import("./Pages/PlusCard/Downloads.tsx"));
const TandemList = lazy(() => import("./Pages/PlusCard/TandemList.tsx"));

const App = () => {
  const { token, logIn, error, loginInProgress }: IAuthContext =
    use(AuthContext);
  const { t } = useTranslation();

  const isLoggedIn = !loginInProgress && !!token; // && sessionValid;
  const hasError = !!error;
  const isLoading = loginInProgress;

  // Only call useUserAuth after authentication is complete (token is available)
  // This prevents premature API calls before OAuth flow completes
  const {
    shouldShowUnauthorized,
    shouldShowSessionExpired,
    isLoadingUserInfo,
    canViewUserManagement,
    canAccessPartnerImport,
    canAccessGSAPImport,
    canAccessOMRAdditionalTabs,
    canAccessGenerateReport,
    userTypeName,
  } = useUserAuth({
    isLoggedIn: isLoggedIn && !!token, // Ensure token exists before making API calls
    isLoading,
    hasError,
  });

  // Check if user is PlusCard type (should not have access to import features)
  const isPlusCardUser = userTypeName === "PlusCard";
  const canAccessPartnerImportWithUserType =
    canAccessPartnerImport && !isPlusCardUser;
  const canAccessGSAPImportWithUserType =
    canAccessGSAPImport && !isPlusCardUser;
  const canAccessOMRAdditionalTabsWithUserType =
    canAccessOMRAdditionalTabs && !isPlusCardUser;
  const routeFallback = (
    <Loader message={t("common.loading", "Loading...")} size="medium" />
  );

  useEffect(() => {
    // Don't interfere with OAuth callback processing
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has("code");
    const hasAuthState = urlParams.has("state");

    if (hasAuthCode || hasAuthState) {
      // OAuth callback detected, letting library handle it
      return;
    }

    // Only trigger login if not authenticated
    if (!isLoggedIn && !isLoading && !hasError) {
      const publicPaths = [LOGOUT, UNAUTHORIZED];
      const currentPath = window.location.pathname;

      if (!publicPaths.includes(currentPath)) {
        // Protected route accessed without authentication
        logIn();
      }
    }
  }, [isLoggedIn, isLoading, hasError, logIn]);

  // Determine what to render based on auth state
  const renderContent = () => {
    if (shouldShowUnauthorized) {
      return <Unauthorized />;
    }

    if (shouldShowSessionExpired) {
      return <SessionExpired />;
    }

    if (isLoggedIn) {
      if (isLoadingUserInfo) {
        return (
          <Loader
            message={t("auth.authorizing", "Authorizing...")}
            size="medium"
          />
        );
      }
      return (
        <ProtectedRoute
          isLoggedIn={isLoggedIn}
          hasError={hasError}
          isLoading={isLoading}
        >
          <InactivityTracker>
            <Layout />
          </InactivityTracker>
        </ProtectedRoute>
      );
    }

    if (isLoading) {
      return <Login />;
    }

    if (hasError) {
      return <Logout />;
    }

    return <Login />;
  };

  return (
    <>
      <BrowserRouter>
        <Suspense fallback={routeFallback}>
          <Routes>
            {/* Public routes */}
            <Route path={LOGOUT} element={<Logout />} />
            <Route path={UNAUTHORIZED} element={<Unauthorized />} />

            {/* Protected routes */}
            <Route path="/" element={renderContent()}>
              <Route index element={<Navigate to={LANDING} replace />} />
              <Route path={LANDING} element={<Landing />} />
              <Route path={MDM} element={<Clusters />}>
                <Route
                  index
                  element={<Navigate to={MDM_VIEW_SITES} replace />}
                />
                <Route path="view-sites" element={<SitesTable />} />
                <Route path="import-sites" element={<ImportSites />} />
                <Route path="import-logs" element={<ImportSiteLogs />} />
                <Route
                  path="compiled-site-data"
                  element={<CompiledSiteDataTable />}
                />
              </Route>
              <Route path={PARTNER} element={<Partners />}>
                <Route
                  index
                  element={<Navigate to={PARTNER_VIEW_CUSTOMERS} replace />}
                />
                <Route path="view-customers" element={<PartnersTable />} />
                <Route
                  path="import-customers"
                  element={
                    canAccessPartnerImportWithUserType ? (
                      <ImportCustomers />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
                <Route
                  path="import-logs"
                  element={
                    canAccessPartnerImportWithUserType ? (
                      <ImportLogs />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
              </Route>

              {/* Pricing routes */}
              <Route path={PRICING} element={<Pricing />}>
                <Route
                  index
                  element={<Navigate to={PRICING_FUEL_PRICES} replace />}
                />
                <Route path="fuel-prices" element={<OMRFuelPriceView />} />
                <Route
                  path="import-prices"
                  element={
                    canAccessOMRAdditionalTabsWithUserType ? (
                      <OMRImportPrices />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
                <Route
                  path="import-logs"
                  element={
                    canAccessOMRAdditionalTabsWithUserType ? (
                      <ImportOMRPriceLogs />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
                <Route
                  path="adblue-lpg-prices"
                  element={
                    canAccessOMRAdditionalTabsWithUserType ? (
                      <AdBlueLPGWrapper />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
              </Route>

              {/* Reports routes */}
              <Route path={REPORTS}>
                <Route
                  index
                  element={<Navigate to={REPORTS_ACCESS_DECLARATION} replace />}
                />
                <Route path="access" element={<Reports />}>
                  <Route
                    index
                    element={
                      <Navigate to={REPORTS_ACCESS_DECLARATION} replace />
                    }
                  />
                  <Route path="declaration" element={<DeclarationReport />} />
                  <Route path="dealer" element={<DealerReport />} />
                  <Route
                    path="site-overview"
                    element={<SiteOverviewReport />}
                  />
                  <Route path="detailed" element={<DetailedReport />} />
                  <Route path="rebates" element={<RebatesReport />} />
                  <Route path="nextgen" element={<NextGenReport />} />
                </Route>
                <Route
                  path="generate"
                  element={
                    canAccessGenerateReport ? (
                      <ReportGeneration />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
              </Route>

              {/* Admin route - User Management */}
              <Route
                path={ADMIN}
                element={
                  canViewUserManagement ? <Admin /> : <RedirectToLanding />
                }
              />

              {/* Contract Parameters routes */}
              <Route
                path={CONTRACT_PARAMETERS}
                element={<ContractParameters />}
              >
                <Route
                  index
                  element={
                    <Navigate to={CONTRACT_PARAMETERS_PARTNER} replace />
                  }
                />
                <Route path="partner" element={<PartnerLevelContract />} />
                <Route path="site" element={<SiteLevelContract />} />
                <Route path="product" element={<ProductLevelContract />} />
                <Route path="import/*" element={<ContractParameterImport />} />
                <Route
                  path="import-logs"
                  element={<ContractParameterImportLogs />}
                />
              </Route>

              {/* GSAP Volumes routes */}
              <Route path={GSAP_VOLUMES} element={<GSAPVolumes />}>
                <Route
                  index
                  element={<Navigate to={GSAP_VOLUMES_VOLUMES} replace />}
                />
                <Route path="volumes" element={<Volumes />} />
                <Route path="correction" element={<Correction />} />
                <Route
                  path="manual-corrections"
                  element={<ManualCorrections />}
                />
                <Route path="rebates" element={<Rebates />} />
                <Route
                  path="importVolumes"
                  element={
                    canAccessGSAPImportWithUserType ? (
                      <GSAPImport />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
                <Route
                  path="importLogs"
                  element={
                    canAccessGSAPImportWithUserType ? (
                      <GSAPImportLogs />
                    ) : (
                      <RedirectToLanding />
                    )
                  }
                />
              </Route>

              {/* Other routes */}
              <Route path={PRODUCT_DATA} element={<ProductData />} />
              <Route path={REGION_DATA} element={<RegionData />} />

              {/* Site Miscellaneous routes with sub-tabs */}
              <Route path={SITE_MISCELLANEOUS} element={<SiteMiscellaneous />}>
                <Route
                  index
                  element={
                    <Navigate to={SITE_MISCELLANEOUS_VAT_RATES} replace />
                  }
                />
                <Route path="vat-rates" element={<VatRates />} />
                <Route path="plant-details" element={<PlantDetails />} />
                <Route
                  path="auxiliary-site-info"
                  element={<AuxiliarySiteInfo />}
                />
              </Route>

              {/* Legacy routes - redirect to new location */}
              <Route
                path={VAT_RATES}
                element={<Navigate to={SITE_MISCELLANEOUS_VAT_RATES} replace />}
              />
              <Route
                path={PLANT_DETAILS}
                element={
                  <Navigate to={SITE_MISCELLANEOUS_PLANT_DETAILS} replace />
                }
              />
              <Route
                path={AUXILIARY_SITE_INFO}
                element={
                  <Navigate
                    to={SITE_MISCELLANEOUS_AUXILIARY_SITE_INFO}
                    replace
                  />
                }
              />

              <Route path={FUEL_REBATES} element={<FuelRebates />} />
              <Route path={LOYALTY_REBATES} element={<LoyaltyRebates />} />

              {/* PlusCard routes */}
              <Route path={PLUS_CARD}>
                <Route
                  index
                  element={<Navigate to={PLUS_CARD_CARDS_DOWNLOADS} replace />}
                />
                <Route path="cards" element={<CardList />}>
                  <Route
                    index
                    element={
                      <Navigate to={PLUS_CARD_CARDS_DOWNLOADS} replace />
                    }
                  />
                  <Route path="import" element={<ImportCards />} />
                  <Route path="downloads" element={<Downloads />} />
                  <Route path="tandem-list" element={<TandemList />} />
                </Route>
                <Route path="transactions" element={<Transactions />} />
              </Route>
            </Route>

            {/* Catch-all route */}
            <Route
              path="*"
              element={
                shouldShowUnauthorized ? (
                  <Unauthorized />
                ) : shouldShowSessionExpired ? (
                  <SessionExpired />
                ) : isLoggedIn ? (
                  isLoadingUserInfo ? (
                    <Loader
                      message={t("auth.authorizing", "Authorizing...")}
                      size="medium"
                    />
                  ) : (
                    <NotFound />
                  )
                ) : isLoading ? (
                  <Login />
                ) : hasError ? (
                  <Logout />
                ) : (
                  <Login />
                )
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};
export default App;
